import { agreementsApi, agreementsReducer } from "@monorepo/services/api/agreements";
import { authApi, authReducer } from "@monorepo/services/api/auth";
import { cartApi, cartReducer } from "@monorepo/services/api/carts";
import { couponsApi, couponsReducer } from "@monorepo/services/api/coupons";
import { historyApi, historyReducer } from "@monorepo/services/api/history";
import { miscApi, miscReducer } from "@monorepo/services/api/misc";
import { notificationsApi, notificationsReducer } from "@monorepo/services/api/notifications";
import { orderApi, orderReducer } from "@monorepo/services/api/order";
import { referralApi, referralReducer } from "@monorepo/services/api/referral";
import { servicesApi, servicesReducer } from "@monorepo/services/api/services";
import { vouchersApi, vouchersReducer } from "@monorepo/services/api/vouchers";
import { activeCartSliceReducer } from "@monorepo/slices/activeCart";
import { authSliceReducer, logoutAction } from "@monorepo/slices/auth";
import { historicalCartListener, historicalPaymentSliceReducer } from "@monorepo/slices/historicalPayment";
import { historySliceReducer } from "@monorepo/slices/history";
import { miscPersistedSliceReducer, miscSliceReducer } from "@monorepo/slices/misc";
import { notificationsSliceReducer } from "@monorepo/slices/notifications";
import { paymentSliceReducer } from "@monorepo/slices/payments";
import { serviceSliceReducer } from "@monorepo/slices/service";
import { AnyAction, combineReducers, configureStore, Reducer } from "@reduxjs/toolkit";
import { connectRouter, routerMiddleware } from "connected-react-router";
import { createBrowserHistory } from "history";
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from "redux-persist";
import storage from "redux-persist/lib/storage";

const authPersistConfig = {
  key: "auth",
  storage,
};

const activeCartPersistConfig = {
  key: "activeCart",
  storage,
};

const paymentPersistConfig = {
  key: "payment",
  storage,
};

const servicePersistConfig = {
  key: "service",
  storage,
};

const historyPersistConfig = {
  key: "history",
  storage,
};
const miscPersistedPersistConfig = {
  key: "miscPersisted",
  storage,
};
const notificationsPersistConfig = {
  key: "notifications",
  storage,
  blacklist: ["lastActiveNotificationId"],
};

export const browserHistory = createBrowserHistory();

const appReducer = combineReducers({
  paymentSlice: persistReducer(paymentPersistConfig, paymentSliceReducer),
  activeCartSlice: persistReducer(activeCartPersistConfig, activeCartSliceReducer),
  authSlice: persistReducer(authPersistConfig, authSliceReducer),
  notificationsSlice: persistReducer(notificationsPersistConfig, notificationsSliceReducer),
  historySlice: persistReducer(historyPersistConfig, historySliceReducer),
  serviceSlice: persistReducer(servicePersistConfig, serviceSliceReducer),
  miscPersistedSlice: persistReducer(miscPersistedPersistConfig, miscPersistedSliceReducer),
  [authApi.reducerPath]: authReducer,
  [cartApi.reducerPath]: cartReducer,
  [servicesApi.reducerPath]: servicesReducer,
  [vouchersApi.reducerPath]: vouchersReducer,
  [agreementsApi.reducerPath]: agreementsReducer,
  [miscApi.reducerPath]: miscReducer,
  [orderApi.reducerPath]: orderReducer,
  [notificationsApi.reducerPath]: notificationsReducer,
  [historyApi.reducerPath]: historyReducer,
  [referralApi.reducerPath]: referralReducer,
  router: connectRouter(browserHistory),
  miscSlice: miscSliceReducer,
  historicalPaymentSlice: historicalPaymentSliceReducer,
  [couponsApi.reducerPath]: couponsReducer,
});

type TAppReducer = ReturnType<typeof appReducer>;

const rootReducer: Reducer<TAppReducer, AnyAction> = (state, action) => {
  if (logoutAction.match(action)) {
    // eslint-disable-next-line no-param-reassign
    state = undefined;
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  devTools: process.env.NODE_ENV === "development",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          "orderApi/executeQuery/fulfilled",
          "historyApi/executeQuery/fulfilled",
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
        ],
      },
    })
      .concat([
        authApi.middleware,
        cartApi.middleware,
        servicesApi.middleware,
        vouchersApi.middleware,
        agreementsApi.middleware,
        miscApi.middleware,
        orderApi.middleware,
        notificationsApi.middleware,
        historyApi.middleware,
        referralApi.middleware,
        couponsApi.middleware,
      ])
      .concat([routerMiddleware(browserHistory)])
      .prepend(historicalCartListener.middleware),
});

export const persistor = persistStore(store);

export type TRootState = ReturnType<typeof appReducer>;
export type TAppDispatch = typeof store.dispatch;
